import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Shield, Sparkles, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Language } from '../App';
import { PageHero } from '../components/PageHero';
interface SubscriptionProps {
  language: Language;
}
export function Subscription({
  language
}: SubscriptionProps) {
  const isPashto = language === 'pashto';
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px' as const
    },
    transition: {
      duration: 0.6
    }
  };
  return <div className="pt-24 md:pt-28 bg-gray-50 min-h-screen pb-8" data-id="element-626">
      <PageHero language={language} titleEn="Support Pashto" titlePs="د پښتو ملاتړ وکړئ" introEn="Help preserve Pashto language and literature" introPs="د پښتو ژبه او ادب وساتئ" data-id="element-627" />

      {/* Intro */}
      <section className="py-8 px-6" data-id="element-628">
        <div className="max-w-3xl mx-auto text-center" data-id="element-629">
          <motion.div {...fadeIn} data-id="element-630">
            <p className={`text-gray-800 font-medium ${isPashto ? 'font-pashto leading-[2.2] text-lg md:text-xl' : 'text-xl md:text-2xl leading-relaxed'}`} data-id="element-631">
              {isPashto ? 'پاڅون یوازې یوه مجله نه ده. دا د پښتو ژبې او کلتور د ساتنې پلیټفارم دی. ستاسو مرسته موږ سره مرسته کوي چې خپرونو ته دوام ورکړو، لیکوالانو ته ملاتړ وکړو، او خپله رسیدنه پراخه کړو.' : 'Pasoon is more than a magazine. It is a platform for preserving Pashto language and culture. Your support helps us continue publishing, supporting writers, and expanding our reach.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Support Card */}
      <section className="py-8 px-6" data-id="element-632">
        <div className="max-w-xl mx-auto" data-id="element-633">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative bg-white rounded-3xl shadow-2xl shadow-green-900/10 border-2 border-pasoon-muted/20 overflow-hidden" data-id="element-634">
            <div className="h-3 bg-pasoon-muted w-full absolute top-0 left-0" data-id="element-635"></div>

            <div className="p-8 md:p-12 flex flex-col" data-id="element-636">
              {/* Icon + Heading */}
              <div className="flex flex-col items-center text-center mb-8" data-id="element-637">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-5" data-id="element-638">
                  <Heart className="w-8 h-8 text-pasoon-muted" data-id="element-639" />
                </div>
                <h3 className={`text-3xl md:text-4xl font-bold text-pasoon-dark ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-640">
                  {isPashto ? 'د پښتو ملاتړ وکړئ' : 'Support Pashto'}
                </h3>
              </div>

              {/* Description */}
              <p className={`text-gray-600 text-center text-lg mb-8 ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-641">
                {isPashto ? 'زموږ کار د پښتو ودانول او ساتنه ده. که تاسو مورنۍ ژبه سره مینه لرئ، نو مهرباني وکړئ زموږ کار ته ملاتړ وکړئ.' : 'Our work is to promote and preserve Pashto. If you love the Mother Language, please support our work.'}
              </p>

              {/* Highlight note */}
              <div className="flex items-start gap-3 bg-green-50/80 rounded-xl p-5 mb-10" data-id="element-642">
                <Sparkles className="w-5 h-5 text-pasoon-muted flex-shrink-0 mt-0.5" data-id="element-643" />
                <p className={`text-sm text-pasoon-muted font-medium ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-644">
                  {isPashto ? 'هره مرسته مستقیماً د پاڅون راتلونکې ګڼه او پښتو تمویلوي.' : 'Every contribution directly funds the next issue of Pasoon and Pashto.'}
                </p>
              </div>

              {/* CTA */}
              <a href="https://www.paypal.com/ncp/payment/VVWBMJDVRC7W4" target="_blank" rel="noopener noreferrer" className={`w-full flex items-center justify-center gap-3 px-8 py-6 bg-pasoon-muted text-white rounded-2xl font-bold text-xl hover:bg-green-800 transition-all shadow-xl shadow-green-900/20 hover:shadow-green-900/40 hover:-translate-y-1 active:translate-y-0 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-645">
                <Heart className="w-6 h-6" data-id="element-646" />
                {isPashto ? 'د پښتو ملاتړ وکړئ' : 'Support Pashto'}
              </a>

              {/* Trust */}
              <div className={`flex items-center justify-center gap-2 mt-6 text-gray-500 text-sm font-medium ${isPashto ? 'font-pashto' : ''}`} data-id="element-647">
                <Shield className="w-4 h-4" data-id="element-648" />
                {isPashto ? 'خوندي PayPal تادیه' : 'Secure PayPal Payment'}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-8 px-6" data-id="element-649">
        <div className="max-w-3xl mx-auto" data-id="element-650">
          <motion.div {...fadeIn} className="flex flex-col items-center justify-center gap-6" data-id="element-651">
            <p className={`text-gray-700 text-lg text-center ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-652">
              {isPashto ? 'تاسو کولای شئ د بریښنالیک له لارې هم موږ سره اړیکه ونیسئ. تاسو کولای شئ د PayPal له لارې هم مرسته وکړئ.' : 'You can also get in touch with us via email. Contributions can also be made through PayPal.'}
            </p>

            <div className="flex items-center gap-2 text-gray-800 font-bold text-xl bg-white px-6 py-3 rounded-full shadow-sm border border-gray-100" data-id="element-653">
              <Mail className="w-6 h-6 text-pasoon-red" data-id="element-654" />
              <a href="mailto:pasoonmardan@gmail.com" className="hover:text-pasoon-red transition-colors" data-id="element-655">
                pasoonmardan@gmail.com
              </a>
            </div>

            <div className="w-full h-px bg-gray-200 my-2" data-id="element-656"></div>

            <p className={`text-gray-600 text-center ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-657">
              {isPashto ? 'د نورو معلوماتو لپاره زموږ د اړیکې پاڼه وګورئ.' : 'For more information, please visit our contact page.'}
            </p>

            <Link to="/contact" className={`inline-flex items-center gap-2 text-pasoon-muted hover:text-green-800 font-medium transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-658">
              <MapPin className="w-5 h-5" data-id="element-659" />
              {isPashto ? 'د اړیکې پاڼه وګورئ' : 'View Contact Page'}
            </Link>
          </motion.div>
        </div>
      </section>
    </div>;
}